import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { inr, useShop } from "@/context/ShopContext";

export default function ProductCard({ product }) {
  const { toggleWishlist, isWishlisted, ratings } = useShop();
  const rating = ratings[product.id];
  const wished = isWishlisted(product.id);
  const soldOut = product.stock === 0;
  const [spot, setSpot] = useState({ x: 50, y: 30 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <div className="group" data-testid={`product-card-${product.id}`}>
      <div className="relative overflow-hidden bg-beige aspect-[4/5]" onMouseMove={onMove}>
        <Link to={`/product/${product.id}`} aria-label={`View ${product.name} in ${product.shade}`}>
          <img
            src={product.images[0]}
            alt={`${product.name} hijab in ${product.shade}`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          {product.images.length > 1 && (
            <img
              src={product.images[1]}
              alt={`${product.name} in LAMSAH gift packaging`}
              loading="lazy"
              data-testid={`packaging-hover-${product.id}`}
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out hidden md:block"
            />
          )}
          {product.images.length > 1 && !product.preorder && (
            <span
              data-testid={`gift-ready-badge-${product.id}`}
              className="absolute bottom-3 left-3 bg-ivory/95 text-espresso text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 md:group-hover:opacity-0 transition-opacity duration-500"
            >
              Gift-Ready
            </span>
          )}
          {product.friday_only && !product.preorder && (
            <span
              data-testid={`friday-card-badge-${product.id}`}
              className="absolute bottom-3 left-3 bg-espresso text-ivory text-[9px] uppercase tracking-[0.2em] px-2.5 py-1"
            >
              Fridays Only
            </span>
          )}
          {product.preorder && (
            <span
              data-testid={`preorder-card-badge-${product.id}`}
              className="absolute bottom-3 left-3 bg-espresso text-ivory text-[9px] uppercase tracking-[0.2em] px-2.5 py-1"
            >
              Pre-Order
            </span>
          )}
        </Link>
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none hidden md:block"
          style={{ background: `radial-gradient(340px circle at ${spot.x}% ${spot.y}%, rgba(247,241,232,0.22), transparent 65%)` }}
        />
        <div className="absolute inset-3 border border-ivory/0 group-hover:border-ivory/50 transition-colors duration-700 pointer-events-none hidden md:block" />
        {soldOut && (
          <span data-testid={`sold-out-${product.id}`} className="absolute top-3 left-3 bg-ivory/95 text-espresso text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
            Sold out
          </span>
        )}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          onClick={() => toggleWishlist(product.id)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-ivory/90 backdrop-blur-sm flex items-center justify-center transition-transform duration-300 hover:scale-110"
        >
          <Heart
            className={`w-4 h-4 transition-colors duration-300 ${wished ? "fill-rose text-rose" : "text-espresso"}`}
            strokeWidth={1.5}
          />
        </button>
        <Link
          to={`/product/${product.id}`}
          data-testid={`view-product-${product.id}`}
          className="absolute bottom-0 inset-x-0 bg-espresso/90 text-ivory text-[11px] tracking-[0.25em] uppercase text-center py-3.5 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hidden md:block"
        >
          View Product
        </Link>
      </div>
      <Link to={`/product/${product.id}`} className="block pt-4 space-y-1">
        <h3 className="font-serif text-lg leading-snug text-espresso">
          {product.type === "bundle" ? product.name : product.shade}
        </h3>
        <p className="text-[11px] uppercase tracking-[0.2em] text-taupe">
          {product.type === "bundle"
            ? product.shade
            : [product.collection, product.fabric].filter(Boolean).join(" · ") || "Hijab"}
        </p>
        <p className="text-sm text-espresso/80 pt-0.5">{inr(product.price)}</p>
        {rating && rating.count > 0 && (
          <p className="flex items-center gap-1.5 pt-0.5" data-testid={`card-rating-${product.id}`}>
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-3 h-3 ${n <= Math.round(rating.avg) ? "fill-[#C9A227] text-[#C9A227]" : "text-beige"}`} strokeWidth={1.5} />
              ))}
            </span>
            <span className="text-[11px] text-taupe">{rating.avg} ({rating.count})</span>
          </p>
        )}
      </Link>
    </div>
  );
}
